import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mealPlanService } from './meal-plan.service'
import { showToast, showUndoToast } from '@/shared/utils/toast'
import type { WeeklyMealPlan, RemoveMealSlotPayload } from './types'

export function useRemoveMealSlot(weekStartDate: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('meal-plan')
  const timerRefs = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const rejectRefs = useRef<Map<number, (reason?: unknown) => void>>(new Map())

  return useMutation({
    mutationFn: (payload: RemoveMealSlotPayload) =>
      new Promise<void>((resolve, reject) => {
        rejectRefs.current.set(payload.dayOfWeek, reject)
        timerRefs.current.set(
          payload.dayOfWeek,
          setTimeout(async () => {
            try {
              await mealPlanService.removeMealSlot(payload)
              resolve()
            } catch (err) {
              reject(err)
            } finally {
              timerRefs.current.delete(payload.dayOfWeek)
              rejectRefs.current.delete(payload.dayOfWeek)
            }
          }, 3000),
        )
      }),
    onMutate: async (payload) => {
      const queryKey = ['meal-plan', weekStartDate]
      await queryClient.cancelQueries({ queryKey })
      const prev = queryClient.getQueryData<WeeklyMealPlan>(queryKey)

      queryClient.setQueryData<WeeklyMealPlan>(queryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          slots: old.slots.map((slot) =>
            slot.dayOfWeek === payload.dayOfWeek
              ? {
                  ...slot,
                  recipeId: null,
                  recipeName: null,
                  servingSize: null,
                  servingMultiplier: 1.0,
                }
              : slot,
          ),
        }
      })

      showUndoToast(
        t('slot.removeSuccess'),
        t('shell.toast.undo', { ns: 'shell' }),
        () => {
          const timer = timerRefs.current.get(payload.dayOfWeek)
          if (timer) {
            clearTimeout(timer)
            timerRefs.current.delete(payload.dayOfWeek)
          }
          rejectRefs.current.get(payload.dayOfWeek)?.(new Error('undone'))
          rejectRefs.current.delete(payload.dayOfWeek)
          queryClient.setQueryData(queryKey, prev)
        },
      )

      return { prev }
    },
    onError: (err, _payload, ctx) => {
      if (err instanceof Error && err.message === 'undone') return
      queryClient.setQueryData(['meal-plan', weekStartDate], ctx?.prev)
      showToast(t('error.removeFailed'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-plan', weekStartDate] })
    },
  })
}
